/**
 * Interaction Selectors
 *
 * Selectors for checking interaction suppression state.
 * Used to prevent click-through when closing panels via click-outside.
 *
 * @module store/selectors/interactionSelectors
 */

import type { GeoMarkupStore } from '../index';

/**
 * Check if map interactions are currently suppressed
 *
 * Used by click handlers to skip actions during panel close animations.
 * Returns true if current time is before suppression expiry timestamp.
 *
 * IMPORTANT: Use getState() pattern in callbacks, not React subscription
 * (similar to isDoubleClickInProgress handling in useMapEventHandlers.ts)
 *
 * @example
 * ```typescript
 * // In event handler callback:
 * if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) {
 *   return; // Skip action - panel just closed
 * }
 * ```
 */
export const selectIsInteractionSuppressed = (state: GeoMarkupStore): boolean => {
  // Defensive check - state or ui might be undefined during initialization
  if (!state?.ui) return false;

  const until = state.ui.interactionSuppressedUntil;
  // Use == to catch both null and undefined
  if (until == null) return false;
  return Date.now() < until;
};
